import {
  getActiveMunicipalityId,
  getResolvedActiveMunicipalityId,
  setActiveMunicipalityId,
} from "./activeMunicipality";
import {
  activeMunicipalityIdSelector,
  municipalityStateSelector,
} from "@/store/selectors/municipalitySelector";
import {
  refreshMunicipalities,
  setActiveMunicipality,
  updateMunicipalities,
} from "@/store/reducers/municipalitySlice";
import type { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import userSelector from "@/store/selectors/userSelector";

export const useGetMunicipalities = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(userSelector);
  const municipalityState = useSelector(municipalityStateSelector);
  const accountType = user?.type ?? user?.accountType;
  const canLoadMunicipalities =
    enabled &&
    accountType === "client" &&
    !!user?.verifiedEmail &&
    !!user?.verifiedByAdmin;
  const resolvedActiveMunicipalityId = getResolvedActiveMunicipalityId(
    municipalityState.data,
    municipalityState.activeId ?? getActiveMunicipalityId()
  );

  useEffect(() => {
    if (canLoadMunicipalities && !municipalityState.updated && !municipalityState.loading) {
      void dispatch(updateMunicipalities());
    }
  }, [
    canLoadMunicipalities,
    dispatch,
    municipalityState.loading,
    municipalityState.updated,
  ]);

  useEffect(() => {
    if (!canLoadMunicipalities) return;

    if (resolvedActiveMunicipalityId !== municipalityState.activeId) {
      dispatch(setActiveMunicipality(resolvedActiveMunicipalityId));
    }

    if (resolvedActiveMunicipalityId !== getActiveMunicipalityId()) {
      setActiveMunicipalityId(resolvedActiveMunicipalityId);
    }
  }, [
    canLoadMunicipalities,
    dispatch,
    municipalityState.activeId,
    resolvedActiveMunicipalityId,
  ]);

  return {
    data: municipalityState.data ?? [],
    error: municipalityState.error,
    isFetching: municipalityState.loading,
    isLoading: municipalityState.loading,
    refetch: () => dispatch(refreshMunicipalities()),
  };
};

export const useGetActiveMunicipality = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data } = useGetMunicipalities({ enabled });
  const activeMunicipalityId = useSelector(activeMunicipalityIdSelector);
  return (
    activeMunicipalityId ??
    getResolvedActiveMunicipalityId(data, getActiveMunicipalityId())
  );
};
